/* ============================================================
   DRAG A MOVING ROW BACK

   Devin, 10 Aug: "when you hold to pause it would be cool to also be able to
   scroll from side to side. so if something goes by you can bring it back."

   Used by the two homepage rows — the soaps (custom-flavor-ribbon) and the
   cards (custom-card-ribbon). Both are the same shape: a section that hides
   its overflow, a viewport this file translates, and inside it a track that a
   CSS keyframe animates.

   WHY THE DRAG AND THE DRIFT ARE ON DIFFERENT ELEMENTS. Both are a transform,
   and an element has exactly one. Putting the drag on the animated track would
   mean either fighting the keyframe every frame or taking the animation over in
   JavaScript and reimplementing it — so the drift stays a CSS animation on the
   track, the drag is a transform on the viewport around it, and the two
   compose without either knowing about the other.

   THE OFFSET WRAPS, WHICH IS THE WHOLE TRICK. The row is one strip of content
   rendered three times, so it repeats every strip + gap. Keeping the drag offset
   inside one period means a drag can go on forever without ever reaching an
   end, and the jump back is invisible because the pixel at x is identical to
   the pixel at x + period. Without it you can drag the row off its own edge and
   find white space.

   IT PAUSES BY ADDING THE CLASS THE SECTIONS ALREADY USE (is-held), so the
   pause affordance and the drag are the same gesture rather than two systems
   with opinions about the same animation.
   ============================================================ */
(function () {
  'use strict';

  /* Far enough to mean "I am scrubbing the row", short enough that a tap with a
     shaky finger still opens the card. */
  var DRAG_SLOP = 6;

  function init(section) {
    if (section.dataset.ffgDragBound) return;

    var viewport = section.querySelector('[data-ffg-drag-viewport]');
    var strip = section.querySelector('[data-ffg-drag-strip]');
    var track = section.querySelector('[data-ffg-drag-track]');
    if (!viewport || !strip || !track) return;

    section.dataset.ffgDragBound = '1';

    var offset = 0;
    var period = 0;
    var dragging = false;
    var lastX = 0;
    var travelled = 0;

    /* strip + gap, because the second copy starts one gap after the first ends.
       Measured rather than computed from the settings: the strip's width is the
       sum of every card and every gap in it, and only the browser knows that
       once the images have laid out. */
    function measure() {
      var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
      period = strip.getBoundingClientRect().width + gap;
    }

    function apply() {
      viewport.style.transform = offset ? 'translate3d(' + offset + 'px, 0, 0)' : '';
    }

    function shift(dx) {
      offset += dx;
      if (period > 0) {
        while (offset <= -period) offset += period;
        while (offset > 0) offset -= period;
      }
      apply();
    }

    measure();
    window.addEventListener('resize', measure);
    /* Images arrive after first layout and change the strip's width with them. */
    window.addEventListener('load', measure);

    function onMove(event) {
      if (!dragging) return;
      var dx = event.clientX - lastX;
      lastX = event.clientX;
      travelled += Math.abs(dx);
      shift(dx);
    }

    function release() {
      if (!dragging) return;
      dragging = false;
      section.classList.remove('is-held');
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', release);
      window.removeEventListener('pointercancel', release);
    }

    section.addEventListener(
      'pointerdown',
      function (event) {
        if (event.button != null && event.button !== 0) return;
        if (!period) measure();
        dragging = true;
        travelled = 0;
        lastX = event.clientX;
        section.classList.add('is-held');

        /* NO setPointerCapture, AND THAT IS THE POINT (Devin, 10 Aug: "i love
           how i can scroll the cards now but clicking on them does nothing").

           Capture was here so a fast drag that left the row still steered it.
           It also retargets everything that follows to the capturing element —
           including the CLICK. Every click on a card was being delivered to the
           section instead of the <a> inside it, so the browser had no link to
           follow and the card was dead. Confirmed by logging click targets: the
           target came back as div.ffg-cards-section, never the anchor.

           Listening on the window for the rest of the gesture gives the same
           "keeps steering past the edge" behaviour, and leaves the click alone
           to land where it was aimed. */
        window.addEventListener('pointermove', onMove, { passive: true });
        window.addEventListener('pointerup', release, { passive: true });
        /* A touch that turns into a page scroll fires cancel, not up. */
        window.addEventListener('pointercancel', release, { passive: true });
      },
      { passive: true }
    );

    /* A DRAG IS NOT A CLICK. Capture phase, so this runs before the link's own
       handler and before the browser follows the href. The threshold is what
       separates "I dragged the row" from "my finger moved two pixels while
       tapping the tee shirt". */
    section.addEventListener(
      'click',
      function (event) {
        if (travelled > DRAG_SLOP) {
          event.preventDefault();
          event.stopPropagation();
          travelled = 0;
        }
      },
      true
    );

    /* Trackpads and shift-wheel scroll the row too — the same gesture people
       already use on a horizontal list. Only when the intent is clearly
       sideways, so a normal wheel over the row still scrolls the page. */
    section.addEventListener(
      'wheel',
      function (event) {
        var dx = event.deltaX;
        if (event.shiftKey && !dx) dx = event.deltaY;
        if (!dx || Math.abs(dx) < Math.abs(event.deltaY) * 0.6) return;
        event.preventDefault();
        shift(-dx);
      },
      { passive: false }
    );
  }

  function boot() {
    document.querySelectorAll('[data-ffg-drag]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Section re-renders in the theme editor land as new nodes. */
  document.addEventListener('shopify:section:load', boot);
})();
